/**
 * Події браузера, що означають «звʼязок міг зникнути».
 *
 * У браузері таких джерел більше, ніж на мобільному: мобільний Safari прибиває
 * сокети у фоні, а nginx не задає `proxy_read_timeout` для `/ws`, тож зʼєднання
 * може тихо закритися за таймаутом простою. Усі ці події навмисно зводяться в
 * ОДИН колбек — той самий, куди приходять стани зʼєднання і закриття сокета.
 * Guard відсіє зайве; окремі шляхи відновлення для кожного джерела — це рівно
 * та помилка, яку вже оплачено на мобільному клієнті.
 */
export interface LivenessTarget {
  addEventListener: (type: string, listener: () => void) => void;
  removeEventListener: (type: string, listener: () => void) => void;
}

export interface LivenessOptions {
  documentTarget?: LivenessTarget & { visibilityState?: DocumentVisibilityState };
  windowTarget?: LivenessTarget;
}

/**
 * Підписує `onSuspectedLoss` на повернення вкладки, відновлення мережі й
 * приховування сторінки. Повертає функцію відписки — її виклик обовʼязковий
 * при завершенні дзвінка, інакше слухачі переживуть сесію.
 */
export function observeLiveness(
  onSuspectedLoss: () => void,
  onHidden: () => void = () => undefined,
  options: LivenessOptions = {},
): () => void {
  const doc = options.documentTarget ?? (typeof document === 'undefined' ? null : document);
  const win = options.windowTarget ?? (typeof window === 'undefined' ? null : window);

  const handleVisibility = (): void => {
    const state = doc && 'visibilityState' in doc ? doc.visibilityState : undefined;
    if (state === 'hidden') {
      onHidden();
      return;
    }
    // Повернення до вкладки: сокет міг не пережити фон.
    onSuspectedLoss();
  };

  const handleOnline = (): void => {
    onSuspectedLoss();
  };

  const handlePageHide = (): void => {
    onHidden();
  };

  doc?.addEventListener('visibilitychange', handleVisibility);
  win?.addEventListener('online', handleOnline);
  win?.addEventListener('pagehide', handlePageHide);

  return (): void => {
    doc?.removeEventListener('visibilitychange', handleVisibility);
    win?.removeEventListener('online', handleOnline);
    win?.removeEventListener('pagehide', handlePageHide);
  };
}
